import { useLazyQuery } from '@apollo/client';
import {
  EmojiPicker,
  getTranslatedText,
  AI_BOT_STATE,
  getAIRephraseKeys,
  GLOBAL_AI_BOT_STATE
} from '@nile/components';
import {
  AIComponent,
  TicketSearchDropdown,
  withErrorBoundary
} from '@nile/components/src';
import GlobalAIBot from '@nile/components/src/Components/AI/components/GlobalAIBot';
import { EventEmitter, dompurifier } from '@nile/components/src/Utils/helpers';
import { GET_TICKET_AUDIT_LIST, TEST_AI_PROMPT } from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Modal,
  Page,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { cloneDeep, debounce, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// TODO:ASH Add lokalise keys

const PROMPT_TIPS = [
  {
    badgeVariant: 'critical',
    badgeText: 'settings.ai.page.prompt.tip.persona'
  },
  {
    badgeVariant: 'message',
    badgeText: 'settings.ai.page.prompt.tip.context'
  },
  {
    badgeVariant: 'success',
    badgeText: 'settings.ai.page.prompt.tip.task'
  },
  {
    badgeVariant: 'warning',
    badgeText: 'settings.ai.page.prompt.tip.exemplar'
  },
  {
    badgeVariant: 'info',
    badgeText: 'settings.ai.page.prompt.tip.format'
  },
  {
    badgeVariant: 'attention',
    badgeText: 'settings.ai.page.prompt.tip.tone'
  }
];

const AIMessage = (props) => {
  const { message, loading, hidden } = props;

  const [riveState, setRiveState] = useState(GLOBAL_AI_BOT_STATE.entry);

  const updateRiveState = (update) => {
    if (riveState !== update) {
      setRiveState(update);
    }
  };

  const classNames = [
    'flex actr jctr col g20 ai-prompt-message-wraper',
    hidden ? 'inactive' : 'active'
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (loading) {
      updateRiveState(GLOBAL_AI_BOT_STATE.Search);
    } else {
      if (riveState === GLOBAL_AI_BOT_STATE.Search) {
        setTimeout(() => {
          updateRiveState(GLOBAL_AI_BOT_STATE.Happy);
        }, 500);
      }
    }
  }, [loading]);

  return (
    <div className={classNames}>
      <div className="ai-prompt-bot">
        <GlobalAIBot riveState={riveState} stateMachine="Monica_Bot" />
      </div>

      <div>
        <FormattedMessage id={message} />
      </div>
    </div>
  );
};

const AIPromptEditor = (props) => {
  const { editMode, prompt, group, feature, saveLoading, disabled } = props;
  const intl = useIntl();
  const [active, setActive] = useState(editMode || false);
  const handleChange = useCallback(() => setActive(!active), [active]);

  const [promptName, setPromptName] = useState(prompt?.name || null);
  const [promptDescription, setPromptDescription] = useState(
    prompt?.description || null
  );
  const [AIPrompt, setAIPrompt] = useState(
    prompt?.aiPrompt || prompt?.prompt?.prompt || null
  );
  const [ticketConversations, setTicketConversations] = useState(null);
  const [emojiData, setEmojiData] = useState(null);

  const onEmojiChange = (emoji) => {
    setEmojiData(emoji);
  };

  const [getTicketAuditList, { loading: ticketFeedsLoading }] = useLazyQuery(
    GET_TICKET_AUDIT_LIST,
    {
      fetchPolicy: 'no-cache',
      onCompleted(res) {
        if (res?.getTicketAuditList?.ticketAudit) {
          const conversationData = cloneDeep(
            res?.getTicketAuditList?.ticketAudit || []
          );
          setTicketConversations(conversationData.slice(-5));
        }
      },
      onError() {}
    }
  );

  const onTicketSelect = (data) => {
    getTicketAuditList({
      variables: {
        input: {
          ticket: { ticketId: data?.entityId },
          listInfo: {
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'conversation.type',
                  operator: 'is',
                  value: 'TECH_REPLY'
                }
              ]
            }
          }
        }
      }
    });
  };

  const [errors, setErrors] = useState(null);

  const validateForm = () => {
    let errorData = {};
    if (!promptName) {
      errorData = {
        ...errorData,
        promptName: getTranslatedText(
          intl,
          'settings.ai.prompt.editor.name.error'
        )
      };
    }

    if (!promptDescription) {
      errorData = {
        ...errorData,
        promptDescription: getTranslatedText(
          intl,
          'settings.ai.prompt.editor.description.error'
        )
      };
    }

    if (!AIPrompt) {
      errorData = {
        ...errorData,
        AIPrompt: getTranslatedText(
          intl,
          'settings.ai.prompt.editor.aiPrompt.error'
        )
      };
    }

    if (isEmpty(errorData)) {
      return null;
    } else {
      return errorData;
    }
  };

  const aiRephraseKeys = getAIRephraseKeys(intl);

  const onClose = () => {
    setPromptName(null);
    setPromptDescription(null);
    setAIPrompt(null);
    setTicketConversations(null);
    setEmojiData(null);
    setActive(false);
    setErrors(null);
    if (props?.onClose) {
      props?.onClose();
    }
  };

  const onSave = () => {
    const emoji = emojiData?.[0];

    const formErrors = validateForm();

    if (formErrors) {
      setErrors(formErrors);
      return;
    } else {
      setErrors(null);
    }

    const data = {
      name: promptName,
      description: promptDescription,
      prompt: {
        prompt: AIPrompt
      },
      displayIcon: emoji
    };

    if (props?.onSave) {
      props?.onSave({
        data: data,
        group: group,
        feature: feature,
        onClose: onClose
      });
    }
  };

  useEffect(() => {
    // TODO: Handle with ref
    EventEmitter.subscribe('closeAIPromptEditor', onClose);
    return function cleanup() {
      EventEmitter.unsubscribe('closeAIPromptEditor');
    };
  }, []);

  return (
    <>
      {!editMode && (
        <Button
          onClick={handleChange}
          icon={<SVGIcon name="Add" />}
          primary
          disabled={disabled}
        >
          <FormattedMessage id="common.prompt" />
        </Button>
      )}

      {active && (
        <Modal open={active} onClose={onClose} showCloseButton={false} large>
          <Page
            title={
              editMode
                ? getTranslatedText(intl, 'settings.ai.page.prompt.edit.title')
                : getTranslatedText(
                    intl,
                    'settings.ai.page.prompt.create.title'
                  )
            }
            contentHeight="110px"
            fullWidth
            primaryAction={
              <ButtonGroup>
                <Button plainSecondary onClick={onClose}>
                  <FormattedMessage id="common.cancel" />
                </Button>
                <Button
                  primary
                  icon={<SVGIcon name="Save" />}
                  onClick={onSave}
                  loading={saveLoading}
                >
                  <FormattedMessage id="common.save" />
                </Button>
              </ButtonGroup>
            }
            // variant="secondary"
          >
            <div className="vh70" id="page-wrapper">
              <div
                // className="pt20 pl40 pr40 pb30"
                id="wrapper"
              >
                <div>
                  <div className="flex w100 actr g5">
                    <EmojiPicker
                      onChange={onEmojiChange}
                      emoji={!isEmpty(prompt?.emoji) ? [prompt?.emoji] : null}
                      size="lg"
                    />
                    <div className="w100">
                      <TextField
                        placeholder={getTranslatedText(
                          intl,
                          'settings.ai.page.prompt.name.placeholder'
                        )}
                        onChange={setPromptName}
                        value={promptName}
                        type="text"
                        fontSize="fontMedium"
                        error={errors?.promptName}
                      />
                    </div>
                  </div>

                  {/* Desc */}
                  <div id="prompt-desc" className="mt10">
                    <TextField
                      placeholder={getTranslatedText(
                        intl,
                        'settings.ai.page.prompt.description.placeholder'
                      )}
                      onChange={setPromptDescription}
                      value={promptDescription}
                      error={errors?.promptDescription}
                    />
                  </div>

                  <div className="mt40" id="step-1">
                    <div className="flex g10 mb10">
                      <div>
                        <Badge status="info">
                          <FormattedMessage id="common.step" /> 1
                        </Badge>
                      </div>
                      <div>
                        <TextStyle weight="SemiBold">
                          {editMode ? (
                            <FormattedMessage id="settings.ai.page.prompt.edit.label" />
                          ) : (
                            <FormattedMessage id="settings.ai.page.prompt.create.label" />
                          )}
                        </TextStyle>
                      </div>
                    </div>

                    <div>
                      <TextField
                        placeholder={getTranslatedText(
                          intl,
                          'settings.ai.page.prompt.create.placeholder'
                        )}
                        maxHeight="80px"
                        value={AIPrompt}
                        // variant="outline"
                        onChange={setAIPrompt}
                        multiline
                        error={errors?.AIPrompt}
                      />
                    </div>
                  </div>

                  <div className="mt40" id="step-2">
                    <div className="flex g10 mb10">
                      <div>
                        <Badge status="info">
                          <FormattedMessage id="common.step" /> 2
                        </Badge>
                      </div>
                      <div>
                        <TextStyle weight="SemiBold">
                          <FormattedMessage id="settings.ai.page.prompt.test" />
                        </TextStyle>
                      </div>
                    </div>
                    <div>
                      <TicketSearchDropdown onChange={onTicketSelect} />
                    </div>
                  </div>
                </div>

                <div id="ticket-preview" className="x-hidden mt10 height300">
                  <AIMessage
                    message={
                      ticketFeedsLoading
                        ? 'settings.ai.page.prompt.conversations.loading'
                        : !ticketConversations
                        ? 'settings.ai.page.prompt.conversations.placeholder'
                        : 'settings.ai.page.prompt.conversations.results.empty'
                    }
                    loading={ticketFeedsLoading}
                    hidden={
                      !isEmpty(ticketConversations) && !ticketFeedsLoading
                    }
                  />
                  {!isEmpty(ticketConversations) && !ticketFeedsLoading && (
                    <>
                      <div className="flex g10 actr mb20">
                        <div className="flex">
                          <SVGIcon name="AIWand" />
                        </div>

                        <div>
                          <TextStyle weight="Medium">
                            <FormattedMessage id="settings.ai.page.prompt.choose.conversations" />
                          </TextStyle>
                        </div>
                      </div>

                      <div className="flex col g20">
                        {ticketConversations.map((conversation) => {
                          const content = conversation?.conversation?.content;
                          const htmlToDisplay = dompurifier(content, {
                            FORBID_TAGS: ['style']
                          });

                          const parser = new DOMParser();
                          const doc = parser.parseFromString(
                            content,
                            'text/html'
                          );

                          const textContent = doc.body.textContent.trim();

                          return (
                            <div className="flex g20 w100">
                              <div className="w100 ticket-message-content message-content">
                                <Card variant="discussion">
                                  <p
                                    className="p10"
                                    dangerouslySetInnerHTML={{
                                      __html: htmlToDisplay
                                    }}
                                  ></p>
                                </Card>
                              </div>

                              <div className="flex g10">
                                <div className="flex">
                                  <AIComponent
                                    type="REPHRASE"
                                    readOnly
                                    data={{
                                      Api: TEST_AI_PROMPT,
                                      variables: {
                                        prompt: AIPrompt,
                                        text: textContent
                                      },
                                      accessorKey: 'testAIPrompt'
                                    }}
                                    hideCopyPaste
                                    featureName="PROMPT_BUILDER"
                                    headingText={getTranslatedText(
                                      intl,
                                      'common.rephrased.text'
                                    )}
                                    headingIcon={'AIWand'}
                                    loadingText={aiRephraseKeys?.loadingText}
                                    successText={aiRephraseKeys?.successText}
                                    copyButtonText={getTranslatedText(
                                      intl,
                                      'common.insert.text'
                                    )}
                                    copySuccessText={getTranslatedText(
                                      intl,
                                      'common.inserted'
                                    )}
                                    mixpanelEventOnClick={{
                                      event:
                                        'Tested a prompt while creating - Admin'
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div id="prompt-tip" className="border-top pt20 mt20">
                <div
                // className="pl40 pr40 pb20"
                >
                  <TextStyle variation="subdued">
                    <FormattedMessage id="settings.ai.page.prompt.tip.title" />
                  </TextStyle>

                  <div className="mt20">
                    <FormattedMessage id="settings.ai.page.prompt.tip.description" />
                  </div>

                  <div className="mt10 flex g10 fsize12 mb10 wrap">
                    {PROMPT_TIPS.map((tip, index) => (
                      <>
                        <div>
                          <Badge status={tip?.badgeVariant} size="large">
                            <FormattedMessage id={tip?.badgeText} />
                          </Badge>
                        </div>
                        {index !== PROMPT_TIPS?.length - 1 && <div>+</div>}
                      </>
                    ))}
                  </div>

                  <Card variant="secondaryCard" noShadow>
                    <div className="w100 py15 pl10 pr10">
                      <FormattedMessage id="setting.ai.prompt.tip.example" />
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </Page>
        </Modal>
      )}
    </>
  );
};

export default withErrorBoundary(AIPromptEditor);
